import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import PitTile, {
	getPitTileOrientation,
	getPitTileType,
	PitTileOrientation,
	PitTileType,
} from 'app/src/engine/tiles/PitTile';
import Tile from 'app/src/engine/tiles/Tile';
import PitTileNode from 'app/src/game/map/tiles/PitTile';
import GamePlugin, { TileData } from 'app/src/plugins/GamePlugin';
import * as React from 'react';

export enum PitsGamePluginError {
	InvalidPitTileTypeInTileDescription,
	InvalidPitTileOrientationInTileDescription,
}

export default class PitsGamePlugin extends GamePlugin {
	// Get a list of Tiles that this plugin provides. When the tile grid is
	// being initialized, it'll ask all plugins which Tiles they create, and
	// later use those corresponding plugins to build Tiles.
	public getListOfProvidedTiles(): string[] {
		return ['PitTile'];
	}

	// Based on Tile data, generate a Tile.
	public getTile(data: TileData): Tile {
		let type: PitTileType | null = null;
		const typeName = data['name'];
		if (typeof typeName === 'string') {
			type = getPitTileType(typeName);
		}
		if (type === null) {
			throw PitsGamePluginError.InvalidPitTileTypeInTileDescription;
		}

		let orientation: PitTileOrientation | null = null;
		const orientationName = data['orientation'];
		if (typeof orientationName === 'string') {
			orientation = getPitTileOrientation(orientationName);
		}
		if (orientation === null) {
			throw PitsGamePluginError.InvalidPitTileOrientationInTileDescription;
		}

		const tile = new PitTile(type, orientation);

		const wallsData = data['walls'];
		if (Array.isArray(wallsData)) {
			tile.addWallsFromData(wallsData);
		}

		return tile;
	}

	// If the editor chooses to support this plugin, and if this plugin displays
	// tiles, this list of tiles will be used in the side panel.
	public getCanonicalTiles(): Tile[] {
		return [new PitTile(PitTileType.Type1_1of1, PitTileOrientation.Up)];
	}

	// Given a Tile, is this plugin capable of generating a corresponding TileNode?
	public providesNodeForTile(tile: Tile): boolean {
		return tile instanceof PitTile;
	}

	// Based on a Tile, generate a TileNode.
	public getTileNode(
		tile: Tile,
		_row: number,
		_col: number,
		_actionResults?: TileActionResult[] | null,
		_completeAnimation?: () => void,
	): React.ReactElement {
		if (!(tile instanceof PitTile)) {
			throw new Error('Unsupported Tile.');
		}

		return <PitTileNode type={tile.type} orientation={tile.orientation} />;
	}
}
